import { EnvironmentProviders, inject, provideEnvironmentInitializer } from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';

/**
 * One modal on screen at a time: when a dialog opens over another, the one underneath (pane and backdrop)
 * is hidden until the new one closes, then shown again exactly as it was — unsaved input intact.
 *
 * Stacked modals read as broken UI (two title bars, two button rows, a doubled backdrop). Hiding rather
 * than closing the lower dialog keeps its state, so a dialog may freely open a sub-dialog (a court
 * request from Edit Eviction Details, a confirmation) without managing this itself.
 *
 * MatDialog runs on the CDK `Dialog`, whose `afterOpened` / `openDialogs` are shared app-wide (a lazy
 * module's `Dialog` delegates to its parent), so one root subscription covers every dialog. A dialog
 * opened without a backdrop is a non-modal panel and leaves what is underneath visible.
 */
export function provideSingleVisibleDialog(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => {
    const dialog = inject(Dialog);
    dialog.afterOpened.subscribe((opened) => {
      const stack = dialog.openDialogs;
      const covered = stack[stack.indexOf(opened) - 1];
      if (!covered || opened.config.hasBackdrop === false) {
        return;
      }
      setCovered(covered, true);
      opened.closed.subscribe(() => {
        // The covered dialog may have been closed meanwhile; only reveal one that is still open.
        if (dialog.openDialogs.includes(covered)) {
          setCovered(covered, false);
        }
      });
    });
  });
}

function setCovered(ref: DialogRef<unknown, unknown>, covered: boolean): void {
  const visibility = covered ? 'hidden' : '';
  // The backdrop is a sibling of the overlay host, not inside it, so both must be toggled.
  [ref.overlayRef.hostElement, ref.overlayRef.backdropElement].forEach((el) => {
    if (el) {
      el.style.visibility = visibility;
    }
  });
}
